/**
 * Normalizes Quill / pasted HTML for public featured job display.
 * Converts emoji-"bullet" paragraphs into real lists, drops empty spacers,
 * and collapses duplicated company About Us blobs.
 */

const BULLET_PREFIX_PATTERN =
  /^(?:▪️|◾|◼|•|●|○|▪|▫|►|▸|–|—|-|📍|\u2022)\s*/u;

const SECTION_LABEL_PATTERN =
  /^(?:Position\s+)?(?:Responsibilities|Requirements|Qualifications|Preferred Qualifications|Basic Qualifications|Nice to [Hh]ave|Key (?:Job )?[Rr]esponsibilities|About(?:\s+Us)?|Description)\s*:?\s*$/u;

const ALLOWED_TAGS = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'I',
  'LI',
  'OL',
  'P',
  'PRE',
  'S',
  'SPAN',
  'STRONG',
  'U',
  'UL',
]);

const BLOCKED_TAGS = new Set([
  'BUTTON',
  'EMBED',
  'FORM',
  'IFRAME',
  'INPUT',
  'MATH',
  'OBJECT',
  'SCRIPT',
  'STYLE',
  'SVG',
  'TEXTAREA',
]);

function normalizeText(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function sanitizeLink(element: Element): void {
  const href = element.getAttribute('href')?.trim() ?? '';
  if (!href) {
    element.removeAttribute('href');
    return;
  }

  try {
    const parsed = new URL(href, 'https://itjobopportunities.io');
    if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
      element.removeAttribute('href');
      return;
    }
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener noreferrer');
    }
  } catch {
    element.removeAttribute('href');
  }
}

function sanitizeRichHtml(container: HTMLElement): void {
  for (const element of Array.from(container.querySelectorAll('*'))) {
    if (BLOCKED_TAGS.has(element.tagName)) {
      element.remove();
      continue;
    }
    if (!ALLOWED_TAGS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      continue;
    }

    for (const attribute of Array.from(element.attributes)) {
      const keepHref = element.tagName === 'A' && attribute.name === 'href';
      const keepTitle = element.tagName === 'A' && attribute.name === 'title';
      const keepListType =
        element.tagName === 'LI' &&
        attribute.name === 'data-list' &&
        ['bullet', 'ordered'].includes(attribute.value);
      const keepQuillClass =
        attribute.name === 'class' &&
        attribute.value
          .split(/\s+/)
          .filter(Boolean)
          .every((className) => className.startsWith('ql-'));

      if (!keepHref && !keepTitle && !keepListType && !keepQuillClass) {
        element.removeAttribute(attribute.name);
      }
    }
    if (element.tagName === 'A') {
      sanitizeLink(element);
    }
  }
}

function isEmptyBlock(element: Element): boolean {
  const text = normalizeText(element.textContent);
  if (text) {
    return false;
  }
  return !element.querySelector('img, video, iframe, table');
}

function stripBulletPrefix(text: string): string {
  return text.replace(BULLET_PREFIX_PATTERN, '').trim();
}

function isBulletParagraph(element: Element): boolean {
  if (element.tagName !== 'P') {
    return false;
  }
  return BULLET_PREFIX_PATTERN.test(normalizeText(element.textContent));
}

function isSectionLabel(element: Element): boolean {
  if (element.tagName !== 'P') {
    return false;
  }
  return SECTION_LABEL_PATTERN.test(normalizeText(element.textContent));
}

function createListFromParagraphs(
  doc: Document,
  paragraphs: Element[]
): HTMLUListElement {
  const list = doc.createElement('ul');
  for (const paragraph of paragraphs) {
    const item = doc.createElement('li');
    item.textContent = stripBulletPrefix(normalizeText(paragraph.textContent));
    list.appendChild(item);
  }
  return list;
}

function convertBulletParagraphs(container: HTMLElement, doc: Document): void {
  const children = Array.from(container.children);
  let index = 0;

  while (index < children.length) {
    const current = children[index];
    if (!isBulletParagraph(current)) {
      index += 1;
      continue;
    }

    const group: Element[] = [];
    let cursor = index;
    while (cursor < children.length && isBulletParagraph(children[cursor])) {
      group.push(children[cursor]);
      cursor += 1;
    }

    const list = createListFromParagraphs(doc, group);
    current.replaceWith(list);
    for (let offset = 1; offset < group.length; offset += 1) {
      group[offset].remove();
    }

    children.splice(index, group.length, list);
    index += 1;
  }
}

function promoteSectionLabels(container: HTMLElement, doc: Document): void {
  for (const child of Array.from(container.children)) {
    if (!isSectionLabel(child)) {
      continue;
    }
    const heading = doc.createElement('h3');
    heading.textContent = normalizeText(child.textContent).replace(/:$/, '');
    child.replaceWith(heading);
  }
}

function removeRedundantLeadHeading(container: HTMLElement): void {
  const first = container.firstElementChild;
  if (
    first &&
    /^H[1-4]$/.test(first.tagName) &&
    /^job description:?$/i.test(normalizeText(first.textContent))
  ) {
    first.remove();
  }
}

function removeEmptyBlocks(container: HTMLElement): void {
  for (const child of Array.from(container.children)) {
    if (isEmptyBlock(child)) {
      child.remove();
    }
  }
}

/**
 * If the content repeats itself (common About Us paste bug), keep the first copy.
 */
function collapseDuplicatedContent(container: HTMLElement): void {
  const children = Array.from(container.children);
  if (children.length < 4) {
    return;
  }

  const texts = children.map((child) => normalizeText(child.textContent));
  const firstIdx = texts.findIndex((text) => text.length >= 24);
  if (firstIdx < 0) {
    return;
  }

  const needle = texts[firstIdx];
  let cutFrom = -1;

  for (let i = firstIdx + 1; i < texts.length; i += 1) {
    const text = texts[i];
    if (!text) {
      continue;
    }

    if (text === needle) {
      cutFrom = i;
      break;
    }

    const nestedAt = text.indexOf(needle);
    if (nestedAt > 0) {
      // "...recruitmentWe are a boutique..." — keep the prefix, drop the rest.
      children[i].textContent = text.slice(0, nestedAt).trim();
      cutFrom = i + 1;
      break;
    }
  }

  if (cutFrom < 0 || cutFrom >= children.length) {
    return;
  }

  for (let i = children.length - 1; i >= cutFrom; i -= 1) {
    children[i].remove();
  }
}

export function normalizeFeaturedRichHtml(
  html: string | undefined | null
): string {
  const source = html?.trim() ?? '';
  if (!source || typeof DOMParser === 'undefined') {
    return source;
  }

  const doc = new DOMParser().parseFromString(
    `<div id="root">${source}</div>`,
    'text/html'
  );
  const container = doc.getElementById('root');
  if (!container) {
    return source;
  }

  sanitizeRichHtml(container);
  removeEmptyBlocks(container);
  convertBulletParagraphs(container, doc);
  promoteSectionLabels(container, doc);
  removeRedundantLeadHeading(container);
  collapseDuplicatedContent(container);
  removeEmptyBlocks(container);

  return container.innerHTML.trim();
}
